import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { sdk } from '@/lib/sdk'
import { showFlashToast } from '@/lib/flashToast'
import { playerApi, saveProgressOnUnload } from './api'
import { registerActivePlayer } from './leave'
import { clearMediaSession, updateMediaSession, updateMediaSessionPosition } from './mediaSession'
import type {
  EpisodeLinkResolver,
  PlayerEpisode,
  PlayerProgressBody,
  PlayerQueueItem,
  SleepTimerChoice
} from './types'

/**
 * F-178 player: one `<audio>` for the whole community session, above the
 * module switch (mounted in `App.tsx` next to the chat). It keeps playing
 * while the person navigates and switches spaces; the provider is keyed by
 * community and session, so switching community (another domain) or account
 * stops it.
 *
 * Progress: every ~10 s while playing, and on pause, end, switch and page
 * exit, `PUT /media-episodes/{id}/progress` with the play's `sessionId` and
 * the seconds really listened in it (seeking does not count). Without those
 * two the server never counts the play nor the completion.
 */

export const PLAYER_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const
export const SKIP_BACK_SECONDS = 15
export const SKIP_FORWARD_SECONDS = 30

const PROGRESS_EVERY_MS = 10_000
const RATE_KEY = 'memberfy:player:rate'
const VOLUME_KEY = 'memberfy:player:volume'
const snapshotKey = (communityId: string) => `memberfy:player:${communityId}`

export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error'

export interface PlayerSleepTimer {
  choice: SleepTimerChoice
  /** Epoch ms when it pauses; null for "end of the episode". */
  endsAt: number | null
}

export interface PlayerState {
  current: PlayerEpisode | null
  status: PlayerStatus
  position: number
  duration: number
  rate: number
  /** 0–1, remembered in this browser. */
  volume: number
  coverUrl: string | null
  sleep: PlayerSleepTimer | null
  queue: PlayerQueueItem[]
  /** Queue and progress need a login; a visitor only listens. */
  canSync: boolean
  /** A full `<PodcastPlayer>` of the current episode is on screen (the mini-player steps aside). */
  fullPlayerVisible: boolean
}

export interface PlayEpisodeOptions {
  /** Second to start at; default the server's `progress.resumeAt` (or 0). */
  startAt?: number | null
  /** Default true. False loads the episode paused (the mini-player shows it). */
  autoplay?: boolean
}

export interface PlayerControls {
  /** Plays an episode (the detail, or just `{ id }`: the detail is fetched). */
  playEpisode: (episode: PlayerEpisode | { id: string }, options?: PlayEpisodeOptions) => Promise<void>
  play: () => void
  pause: () => void
  toggle: () => void
  seek: (seconds: number) => void
  skip: (deltaSeconds: number) => void
  setRate: (rate: number) => void
  setVolume: (volume: number) => void
  setSleepTimer: (choice: SleepTimerChoice | null) => void
  /** Next: the queue's first episode, else the show's next one. */
  next: () => void
  /** Stops and closes the mini-player (the progress is saved first). */
  stop: () => void
  enqueue: (episode: PlayerEpisode | PlayerQueueItem['episode']) => Promise<void>
  removeFromQueue: (episodeId: string) => Promise<void>
  moveInQueue: (episodeId: string, toIndex: number) => Promise<void>
  playFromQueue: (episodeId: string) => Promise<void>
  refreshQueue: () => Promise<void>
  /** Modules tell the player how to link an episode (title in the mini-player, queue rows). */
  setEpisodeLinkResolver: (resolver: EpisodeLinkResolver | null) => void
  episodeHref: (episode: { id: string; showId: string; spaceId: string; standalone?: PlayerEpisode['standalone'] }) => string | null
  /** A full player is showing this episode; returns the cleanup. */
  registerFullPlayer: (episodeId: string) => () => void
}

const PlayerStateContext = React.createContext<PlayerState | null>(null)
const PlayerControlsContext = React.createContext<PlayerControls | null>(null)

const readRate = (): number => {
  try {
    const stored = Number(localStorage.getItem(RATE_KEY))
    return (PLAYER_RATES as readonly number[]).includes(stored) ? stored : 1
  } catch {
    return 1
  }
}

const readVolume = (): number => {
  try {
    const stored = localStorage.getItem(VOLUME_KEY)
    const value = stored === null ? 1 : Number(stored)
    return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1
  } catch {
    return 1
  }
}

const newSessionId = (): string => {
  const bytes = new Uint8Array(12)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

const sourceOf = (episode: PlayerEpisode): string | null =>
  /* A videocast in the Podcast plays the sound of its video file here; the picture is the video player's. */
  episode.media?.audio?.url || episode.media?.video?.url || null

const hasPlayableMedia = (episode: PlayerEpisode | { id: string }): episode is PlayerEpisode =>
  'media' in episode && Boolean(episode.media && sourceOf(episode as PlayerEpisode))

interface Snapshot { episodeId: string; position: number }

const readSnapshot = (communityId: string): Snapshot | null => {
  try {
    const raw = localStorage.getItem(snapshotKey(communityId))
    const parsed = raw ? (JSON.parse(raw) as Snapshot) : null
    return parsed && typeof parsed.episodeId === 'string' ? parsed : null
  } catch {
    return null
  }
}

const writeSnapshot = (communityId: string, snapshot: Snapshot | null) => {
  try {
    if (snapshot) localStorage.setItem(snapshotKey(communityId), JSON.stringify(snapshot))
    else localStorage.removeItem(snapshotKey(communityId))
  } catch {
    /* Private window: the mini-player just does not come back after a reload. */
  }
}

/** The queue row for an episode the person adds (shown before the server answers). */
const queueEpisodeOf = (episode: PlayerEpisode | PlayerQueueItem['episode']): PlayerQueueItem['episode'] =>
  'showTitle' in episode
    ? episode
    : {
        id: episode.id,
        title: episode.title,
        showId: episode.showId,
        showTitle: episode.show?.title || '',
        spaceId: episode.spaceId,
        coverMediaId: episode.coverMediaId || episode.show?.coverMediaId || null,
        durationSeconds: episode.durationSeconds ?? null,
        mediaKind: episode.mediaKind ?? null
      }

interface PlayerProviderProps {
  communityId: string
  isAuthenticated: boolean
  children: React.ReactNode
}

export function PlayerProvider({ communityId, isAuthenticated, children }: PlayerProviderProps) {
  const { t } = useTranslation('player')
  const audioRef = React.useRef<HTMLAudioElement | null>(null)
  if (!audioRef.current && typeof document !== 'undefined') {
    const audio = document.createElement('audio')
    audio.preload = 'metadata'
    audio.volume = readVolume()
    audioRef.current = audio
  }

  const [current, setCurrent] = React.useState<PlayerEpisode | null>(null)
  const [status, setStatus] = React.useState<PlayerStatus>('idle')
  const [position, setPosition] = React.useState(0)
  const [duration, setDuration] = React.useState(0)
  const [rate, setRateState] = React.useState(readRate)
  const [volume, setVolumeState] = React.useState(readVolume)
  const [coverUrl, setCoverUrl] = React.useState<string | null>(null)
  const [sleep, setSleep] = React.useState<PlayerSleepTimer | null>(null)
  const [queue, setQueue] = React.useState<PlayerQueueItem[]>([])
  const [fullPlayers, setFullPlayers] = React.useState<string[]>([])

  /* What the event handlers read without re-binding. */
  const currentRef = React.useRef<PlayerEpisode | null>(null)
  const queueRef = React.useRef<PlayerQueueItem[]>([])
  const sleepRef = React.useRef<PlayerSleepTimer | null>(null)
  const sessionRef = React.useRef<{ id: string; listened: number; lastTime: number | null; lastWall: number; lastSentAt: number }>({ id: newSessionId(), listened: 0, lastTime: null, lastWall: 0, lastSentAt: 0 })
  const pendingSeekRef = React.useRef<number | null>(null)
  const linkResolverRef = React.useRef<EpisodeLinkResolver | null>(null)
  const recoveringRef = React.useRef(0)
  const canSync = isAuthenticated
  const canSyncRef = React.useRef(canSync)
  canSyncRef.current = canSync
  const statusRef = React.useRef<PlayerStatus>('idle')
  const positionRef = React.useRef(0)
  currentRef.current = current
  statusRef.current = status
  positionRef.current = position
  queueRef.current = queue
  sleepRef.current = sleep

  /* ------------------------------------------------------------ progress */

  const progressBody = React.useCallback((positionOverride?: number): PlayerProgressBody | null => {
    const audio = audioRef.current
    const episode = currentRef.current
    if (!audio || !episode) return null
    const total = Number.isFinite(audio.duration) && audio.duration > 0 ? Math.round(audio.duration) : episode.durationSeconds || undefined
    const at = Math.max(0, Math.floor(positionOverride ?? audio.currentTime ?? 0))
    return {
      positionSeconds: total ? Math.min(at, total) : at,
      ...(total ? { durationSeconds: total } : {}),
      sessionId: sessionRef.current.id,
      listenedSeconds: Math.floor(sessionRef.current.listened)
    }
  }, [])

  const sendProgress = React.useCallback((options: { positionOverride?: number; unloading?: boolean } = {}) => {
    const episode = currentRef.current
    const body = progressBody(options.positionOverride)
    if (!episode || !body || episode.standalone) return
    writeSnapshot(communityId, { episodeId: episode.id, position: body.positionSeconds })
    if (!canSyncRef.current) return
    sessionRef.current.lastSentAt = Date.now()
    if (options.unloading) {
      saveProgressOnUnload(episode.id, body)
      return
    }
    playerApi.saveProgress(episode.id, body).catch(() => undefined)
  }, [communityId, progressBody])

  /* ------------------------------------------------------------ loading */

  const resolveCover = React.useCallback(async (episode: PlayerEpisode) => {
    const direct = episode.media?.cover?.url || episode.media?.video?.thumbnailUrl || null
    if (direct) { setCoverUrl(direct); return }
    setCoverUrl(null)
    const showCover = episode.show?.coverMediaId
    if (!showCover) return
    try {
      const signed = await sdk.media.getUrl(showCover)
      if (currentRef.current?.id === episode.id) setCoverUrl(signed?.url || signed?.thumbnailUrl || null)
    } catch {
      /* No cover: the player shows the placeholder. */
    }
  }, [])

  const load = React.useCallback((episode: PlayerEpisode, startAt: number, autoplay: boolean) => {
    const audio = audioRef.current
    const src = sourceOf(episode)
    if (!audio || !src) {
      setStatus('error')
      return
    }
    /* The previous episode's last position goes out before the switch. */
    if (currentRef.current && currentRef.current.id !== episode.id && !audio.paused) sendProgress()
    sessionRef.current = { id: newSessionId(), listened: 0, lastTime: null, lastWall: 0, lastSentAt: Date.now() }
    currentRef.current = episode
    setCurrent(episode)
    setPosition(startAt)
    setDuration(episode.durationSeconds || 0)
    pendingSeekRef.current = startAt > 0 ? startAt : null
    audio.src = src
    audio.defaultPlaybackRate = rate
    audio.playbackRate = rate
    void resolveCover(episode)
    if (!episode.standalone) writeSnapshot(communityId, { episodeId: episode.id, position: startAt })
    if (autoplay) {
      setStatus('loading')
      audio.play().catch(() => setStatus('paused'))
    } else {
      audio.preload = 'metadata'
      audio.load()
      setStatus('paused')
    }
  }, [communityId, rate, resolveCover, sendProgress])

  const playEpisode = React.useCallback(async (input: PlayerEpisode | { id: string }, options: PlayEpisodeOptions = {}) => {
    const autoplay = options.autoplay !== false
    const audio = audioRef.current
    /* Same episode: resume, or jump when asked. */
    if (audio && currentRef.current?.id === input.id && audio.src) {
      if (typeof options.startAt === 'number') audio.currentTime = options.startAt
      if (autoplay) audio.play().catch(() => setStatus('paused'))
      return
    }
    /* Called from a click: the element must start inside the gesture, so a known episode plays at once. */
    if (hasPlayableMedia(input)) {
      load(input, options.startAt ?? input.progress?.resumeAt ?? 0, autoplay)
      return
    }
    setStatus('loading')
    try {
      const detail = await playerApi.getEpisode(input.id)
      if (!sourceOf(detail)) {
        setStatus(currentRef.current ? 'paused' : 'idle')
        showFlashToast(t('errors.noMedia'))
        return
      }
      load(detail, options.startAt ?? detail.progress?.resumeAt ?? 0, autoplay)
    } catch {
      setStatus(currentRef.current ? 'paused' : 'idle')
      showFlashToast(t('errors.load'))
    }
  }, [load, t])

  /* ------------------------------------------------------------ queue */

  const refreshQueue = React.useCallback(async () => {
    if (!canSyncRef.current) return
    try {
      setQueue(await playerApi.getQueue())
    } catch {
      /* Keep what is on screen. */
    }
  }, [])

  const enqueue = React.useCallback(async (episode: PlayerEpisode | PlayerQueueItem['episode']) => {
    if (!canSyncRef.current) return
    const before = queueRef.current
    if (before.some((item) => item.episodeId === episode.id)) return
    const optimistic: PlayerQueueItem = { episodeId: episode.id, position: before.length, access: 'full', addedAt: new Date().toISOString(), episode: queueEpisodeOf(episode) }
    setQueue([...before, optimistic])
    try {
      setQueue(await playerApi.addToQueue(episode.id))
      showFlashToast(t('queue.added'))
    } catch {
      setQueue(before)
      showFlashToast(t('errors.queue'))
    }
  }, [t])

  const removeFromQueue = React.useCallback(async (episodeId: string) => {
    const before = queueRef.current
    setQueue(before.filter((item) => item.episodeId !== episodeId))
    if (!canSyncRef.current) return
    try {
      setQueue(await playerApi.removeFromQueue(episodeId))
    } catch {
      setQueue(before)
      showFlashToast(t('errors.queue'))
    }
  }, [t])

  const moveInQueue = React.useCallback(async (episodeId: string, toIndex: number) => {
    const before = queueRef.current
    const from = before.findIndex((item) => item.episodeId === episodeId)
    if (from < 0) return
    const target = Math.max(0, Math.min(before.length - 1, toIndex))
    if (target === from) return
    const next = [...before]
    const [moved] = next.splice(from, 1)
    next.splice(target, 0, moved)
    setQueue(next)
    try {
      setQueue(await playerApi.setQueue(next.map((item) => item.episodeId)))
    } catch {
      setQueue(before)
      showFlashToast(t('errors.queue'))
    }
  }, [t])

  const playFromQueue = React.useCallback(async (episodeId: string) => {
    /* Playing an episode takes it off the queue ("a seguir" is what is still to come). */
    const before = queueRef.current
    setQueue(before.filter((item) => item.episodeId !== episodeId))
    await playEpisode({ id: episodeId })
    if (canSyncRef.current) playerApi.removeFromQueue(episodeId).then(setQueue).catch(() => undefined)
  }, [playEpisode])

  /* ------------------------------------------------------------ controls */

  const play = React.useCallback(() => {
    const audio = audioRef.current
    if (!audio || !currentRef.current) return
    if (!audio.src) { void playEpisode(currentRef.current); return }
    audio.play().catch(() => setStatus('paused'))
  }, [playEpisode])

  const pause = React.useCallback(() => { audioRef.current?.pause() }, [])

  const toggle = React.useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) play()
    else pause()
  }, [play, pause])

  const seek = React.useCallback((seconds: number) => {
    const audio = audioRef.current
    if (!audio || !currentRef.current) return
    const total = Number.isFinite(audio.duration) ? audio.duration : currentRef.current.durationSeconds || Infinity
    const at = Math.max(0, Math.min(seconds, total))
    if (audio.readyState === 0) pendingSeekRef.current = at
    else audio.currentTime = at
    sessionRef.current.lastTime = null
    setPosition(at)
  }, [])

  const skip = React.useCallback((delta: number) => {
    const audio = audioRef.current
    if (!audio) return
    seek((audio.readyState === 0 ? pendingSeekRef.current ?? 0 : audio.currentTime) + delta)
  }, [seek])

  const setRate = React.useCallback((value: number) => {
    const audio = audioRef.current
    setRateState(value)
    if (audio) { audio.defaultPlaybackRate = value; audio.playbackRate = value }
    try { localStorage.setItem(RATE_KEY, String(value)) } catch { /* not remembered */ }
  }, [])

  const setVolume = React.useCallback((value: number) => {
    const next = Math.min(1, Math.max(0, value))
    setVolumeState(next)
    if (audioRef.current) audioRef.current.volume = next
    try { localStorage.setItem(VOLUME_KEY, String(next)) } catch { /* not remembered */ }
  }, [])

  const setSleepTimer = React.useCallback((choice: SleepTimerChoice | null) => {
    setSleep(choice === null ? null : { choice, endsAt: choice === 'end' ? null : Date.now() + choice * 60_000 })
  }, [])

  const next = React.useCallback(() => {
    const [first] = queueRef.current
    if (first) { void playFromQueue(first.episodeId); return }
    const following = currentRef.current?.next
    if (following) void playEpisode({ id: following.id }, { startAt: null })
  }, [playEpisode, playFromQueue])

  const stop = React.useCallback(() => {
    const audio = audioRef.current
    if (audio && currentRef.current) {
      if (!audio.paused || audio.currentTime > 0) sendProgress()
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
    }
    writeSnapshot(communityId, null)
    currentRef.current = null
    setCurrent(null)
    setStatus('idle')
    setPosition(0)
    setDuration(0)
    setCoverUrl(null)
    setSleep(null)
    clearMediaSession()
  }, [communityId, sendProgress])

  /* A new resolver re-renders the links already on screen (a paused mini-player has no tick of its own). */
  const [linkVersion, setLinkVersion] = React.useState(0)
  const setEpisodeLinkResolver = React.useCallback((resolver: EpisodeLinkResolver | null) => {
    linkResolverRef.current = resolver
    setLinkVersion((n) => n + 1)
  }, [])

  const episodeHref = React.useCallback((episode: { id: string; showId: string; spaceId: string; standalone?: PlayerEpisode['standalone'] }) => {
    void linkVersion
    if (episode.standalone) return episode.standalone.href
    try {
      return linkResolverRef.current?.(episode) ?? null
    } catch {
      return null
    }
  }, [linkVersion])

  const registerFullPlayer = React.useCallback((episodeId: string) => {
    setFullPlayers((list) => [...list, episodeId])
    return () => setFullPlayers((list) => {
      const index = list.indexOf(episodeId)
      return index < 0 ? list : [...list.slice(0, index), ...list.slice(index + 1)]
    })
  }, [])

  /* ------------------------------------------------------------ audio events */

  React.useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onLoadedMetadata = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) setDuration(audio.duration)
      if (pendingSeekRef.current !== null) {
        const total = Number.isFinite(audio.duration) ? audio.duration : Infinity
        /* A resume point in the last seconds would end at once: start over instead. */
        audio.currentTime = pendingSeekRef.current < total - 5 ? pendingSeekRef.current : 0
        pendingSeekRef.current = null
      }
      audio.playbackRate = audio.defaultPlaybackRate
    }
    const onTimeUpdate = () => {
      const now = audio.currentTime
      const session = sessionRef.current
      const wall = performance.now()
      if (!audio.paused && session.lastTime !== null) {
        /* Episode (media) seconds heard, so 15 s of clock at 1.5x is 22.5 s, as the API expects (SEC-336).
           A jump (seek, skip) resets `lastTime` on `seeking`; anything beyond what the clock and the rate
           allow (a seek that slipped through) is not listened time. A throttled background tab still counts. */
        const delta = now - session.lastTime
        const allowed = ((wall - session.lastWall) / 1000) * (audio.playbackRate || 1) + 0.5
        if (delta > 0 && delta <= allowed) session.listened += delta
        const total = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : Infinity
        if (session.listened > total) session.listened = total
      }
      session.lastTime = now
      session.lastWall = wall
      setPosition(now)
      updateMediaSessionPosition(audio)
      if (!audio.paused && Date.now() - session.lastSentAt >= PROGRESS_EVERY_MS) sendProgress()
    }
    const onPlay = () => { sessionRef.current.lastTime = audio.currentTime; sessionRef.current.lastWall = performance.now(); setStatus('playing'); recoveringRef.current = 0 }
    const onPlaying = () => setStatus('playing')
    const onWaiting = () => setStatus('loading')
    const onPause = () => {
      if (audio.ended) return
      setStatus(audio.src ? 'paused' : 'idle')
      if (currentRef.current && audio.src) sendProgress()
    }
    const onSeeking = () => { sessionRef.current.lastTime = null }
    const onRateChange = () => { if (audio.src) setRateState(audio.playbackRate) }
    const onEnded = () => {
      const total = Number.isFinite(audio.duration) ? Math.round(audio.duration) : undefined
      sendProgress({ positionOverride: total })
      const episode = currentRef.current
      if (episode && !episode.standalone) writeSnapshot(communityId, { episodeId: episode.id, position: 0 })
      setStatus('paused')
      if (sleepRef.current?.choice === 'end') {
        setSleep(null)
        return
      }
      const [first] = queueRef.current
      if (first) { void playFromQueue(first.episodeId); return }
      if (episode?.next) void playEpisode({ id: episode.next.id }, { startAt: null })
    }
    const onError = () => {
      const episode = currentRef.current
      if (!episode || !audio.getAttribute('src')) return
      /* The signed link lasts an hour: fetch a fresh one and pick up where it stopped, once a minute at most. */
      const at = audio.currentTime || pendingSeekRef.current || positionRef.current
      if (Date.now() - recoveringRef.current < 60_000) { setStatus('error'); return }
      recoveringRef.current = Date.now()
      const wasPlaying = statusRef.current === 'playing' || statusRef.current === 'loading'
      const standalone = episode.standalone
      const fresh: Promise<PlayerEpisode> = standalone
        ? sdk.media.getUrl(standalone.mediaId).then((signed) => ({ ...episode, media: { audio: signed, video: null, cover: episode.media?.cover ?? null } }))
        : playerApi.getEpisode(episode.id)
      fresh
        .then((next) => { if (currentRef.current?.id === episode.id) load(next, at, wasPlaying) })
        .catch(() => setStatus('error'))
    }
    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('play', onPlay)
    audio.addEventListener('playing', onPlaying)
    audio.addEventListener('waiting', onWaiting)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('seeking', onSeeking)
    audio.addEventListener('ratechange', onRateChange)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('error', onError)
    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('playing', onPlaying)
      audio.removeEventListener('waiting', onWaiting)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('seeking', onSeeking)
      audio.removeEventListener('ratechange', onRateChange)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('error', onError)
    }
  }, [communityId, load, playEpisode, playFromQueue, sendProgress])

  /* ------------------------------------------------------------ sleep timer */

  React.useEffect(() => {
    if (!sleep?.endsAt) return
    const wait = Math.max(0, sleep.endsAt - Date.now())
    const timer = window.setTimeout(() => {
      audioRef.current?.pause()
      setSleep(null)
    }, wait)
    return () => window.clearTimeout(timer)
  }, [sleep])

  /* ------------------------------------------------------------ media session */

  React.useEffect(() => {
    if (!current) return
    updateMediaSession(current, coverUrl, {
      play,
      pause,
      seekBackward: () => skip(-SKIP_BACK_SECONDS),
      seekForward: () => skip(SKIP_FORWARD_SECONDS),
      seekTo: seek,
      next: queue.length > 0 || current.next ? next : null,
      stop
    })
  }, [current, coverUrl, play, pause, skip, seek, next, stop, queue.length])

  /* ------------------------------------------------------------ lifecycle */

  /* Restore the last episode of this community, paused, after a reload or a new tab. */
  React.useEffect(() => {
    const snapshot = readSnapshot(communityId)
    if (!snapshot) return
    let canceled = false
    playerApi.getEpisode(snapshot.episodeId)
      .then((detail) => {
        if (canceled || currentRef.current || !sourceOf(detail)) return
        load(detail, snapshot.position || detail.progress?.resumeAt || 0, false)
      })
      .catch(() => writeSnapshot(communityId, null))
    return () => { canceled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per community session
  }, [communityId])

  /* The queue: on start, and again when the tab comes back (another device may have changed it). */
  React.useEffect(() => {
    if (!canSync) { setQueue([]); return }
    void refreshQueue()
    let last = Date.now()
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 30_000) return
      last = Date.now()
      void refreshQueue()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [canSync, refreshQueue])

  /* Leaving the page (reload, closing the tab, another domain): the last position, with keepalive. */
  React.useEffect(() => {
    const onPageHide = () => {
      const audio = audioRef.current
      if (audio && currentRef.current && audio.src && (!audio.paused || audio.currentTime > 0)) sendProgress({ unloading: true })
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [sendProgress])

  /* Any other sound of the page (a video, a lesson, a Drop) pauses the podcast instead of talking over it. */
  React.useEffect(() => {
    const onOtherPlay = (event: Event) => {
      const audio = audioRef.current
      if (audio && event.target !== audio && event.target instanceof HTMLMediaElement && !event.target.muted && !audio.paused) audio.pause()
    }
    document.addEventListener('play', onOtherPlay, true)
    return () => document.removeEventListener('play', onOtherPlay, true)
  }, [])

  /* The explicit stop for the navigations that still reload the page (`leave.ts`). */
  React.useEffect(() => registerActivePlayer(() => {
    const audio = audioRef.current
    if (!audio || !currentRef.current || !audio.src) return
    sendProgress({ unloading: true })
    audio.pause()
  }), [sendProgress])

  /* Switching community or account unmounts the provider: the sound stops with it. */
  React.useEffect(() => {
    const audio = audioRef.current
    return () => {
      if (!audio) return
      if (currentRef.current && audio.src && !audio.paused) sendProgress({ unloading: true })
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
      clearMediaSession()
    }
  }, [sendProgress])

  const fullPlayerVisible = Boolean(current && fullPlayers.includes(current.id))

  const state = React.useMemo<PlayerState>(() => ({
    current, status, position, duration, rate, volume, coverUrl, sleep, queue, canSync, fullPlayerVisible
  }), [current, status, position, duration, rate, volume, coverUrl, sleep, queue, canSync, fullPlayerVisible])

  const controls = React.useMemo<PlayerControls>(() => ({
    playEpisode, play, pause, toggle, seek, skip, setRate, setVolume, setSleepTimer, next, stop,
    enqueue, removeFromQueue, moveInQueue, playFromQueue, refreshQueue,
    setEpisodeLinkResolver, episodeHref, registerFullPlayer
  }), [playEpisode, play, pause, toggle, seek, skip, setRate, setVolume, setSleepTimer, next, stop,
    enqueue, removeFromQueue, moveInQueue, playFromQueue, refreshQueue,
    setEpisodeLinkResolver, episodeHref, registerFullPlayer])

  return (
    <PlayerControlsContext.Provider value={controls}>
      <PlayerStateContext.Provider value={state}>{children}</PlayerStateContext.Provider>
    </PlayerControlsContext.Provider>
  )
}

/** The player's controls only: stable, so a list of episodes does not re-render with the clock. */
export function usePlayerControls(): PlayerControls {
  const controls = React.useContext(PlayerControlsContext)
  if (!controls) throw new Error('usePlayerControls must be used inside <PlayerProvider>')
  return controls
}

/** The player's state and controls (`current`, `status`, `position`… and `playEpisode`, `enqueue`…). */
export function usePlayer(): PlayerState & PlayerControls {
  const state = React.useContext(PlayerStateContext)
  const controls = React.useContext(PlayerControlsContext)
  if (!state || !controls) throw new Error('usePlayer must be used inside <PlayerProvider>')
  return { ...state, ...controls }
}

/** Null outside the shell (the public pages): for components that may render there. */
export function useOptionalPlayer(): (PlayerState & PlayerControls) | null {
  const state = React.useContext(PlayerStateContext)
  const controls = React.useContext(PlayerControlsContext)
  return state && controls ? { ...state, ...controls } : null
}
